import { Divider, Modal } from "antd";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { UploadImageForm } from "~components/Images";
import PermissionCheck from "~Common/PermissionCheck";
import { useUserPermissions } from "~context/UserPermissionsContext";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { Image } from "~types/image.ts";
import "./LogoUploadModal.scss";
import { ImageTypes } from "~enums/imageTypes";
import { ImageBaseTypes } from "~enums/imageBaseTypes";

type Props = {
  currentLogo: Image;
  organizationId: string;
  refetch: () => void;
};

export const LogoUploadModal = ({ currentLogo, organizationId, refetch }: Props) => {
  const [open, setOpen] = useState(false);
  const { formatMessage } = useIntl();
  const { hasPermission } = useUserPermissions();
  const hasEditPermission = hasPermission(PermissionNames.ORGANIZATION, PermissionOperations.UPDATE);
  const uploadButtonClickHandler = useMemo(
    () => hasEditPermission ? () => setOpen(true) : undefined,
    [hasEditPermission],
  );

  return (
    <>
      <Modal
        title={
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <i className={`fas fa-camera`} />
            {formatMessage({ id: "upsert.organization.logo.modal.title" })}
          </div>
        }
        open={open}
        width={350}
        okButtonProps={{ hidden: true }}
        cancelButtonProps={{ hidden: true }}
        onCancel={() => {
          setOpen(false);
        }}
        afterClose={refetch}
      >
        <>
          <div>{formatMessage({ id: "upsert.organization.logo.modal.description" })}</div>
          <Divider style={{ margin: "5px 0" }} />
          <UploadImageForm
            imageType={ImageTypes.Logo}
            baseId={organizationId}
            initialImage={currentLogo}
            baseType={ImageBaseTypes.Organization}
            permissionKey={PermissionNames.ORGANIZATION}
          />
        </>
      </Modal>
      <div
        className="image-upload-modal-button"
        onClick={uploadButtonClickHandler}
      >
        {currentLogo ? (
          <img className="logo-preview" width={80} height={80} alt="Organization Logo" src={`${currentLogo.url}?size=200`} />
        ) : (
          <div className="upload-logo-preview">
            <div className="upload-logo-text">
              {formatMessage({ id: "upload.logo" })}
            </div>
            <div className="upload-logo-icon">
              <i className={`fas fa-2x fa-shield-halved`} />
            </div>
          </div>
        )}
        <PermissionCheck permissionKeys={[PermissionNames.ORGANIZATION]} permissionOptions={[PermissionOperations.UPDATE]}>
          <div className="camera-icon">
            <i className={`fa-solid fa-2xs fa-camera`} />
          </div>
        </PermissionCheck>
      </div>
    </>
  );
};
